// Practice — tests the STUDENT generated themselves (via Create Test) from
// the published PYQ bank, plus their most recent attempt score (doubling as
// this list's history view). Scoped to quizzes.teacher_id == this student's
// own id (the field name is a holdover from the teacher flow that built
// this endpoint first).
import React, { useEffect, useMemo, useState } from 'react';
import { AccentRule } from './studentUi';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ChevronRight, Award, Clock, AlertTriangle, Plus, ArrowLeft } from 'lucide-react';
import api from './studentApi';
import { getExamPrepColors } from './examPrepTheme';
import { useStudent } from './StudentApp';
import { friendlyError } from '../../lib/errors';
import Loader from '../../components/Loader';

export default function MyQuizzesPage() {
  const navigate = useNavigate();
  const { me, theme } = useStudent();
  const colors = getExamPrepColors(theme?.id);
  const [quizzes, setQuizzes] = useState(null);
  const [attemptsByQuiz, setAttemptsByQuiz] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!me?.id) return;
    let alive = true;
    Promise.all([
      api.get(`/quizzes?teacher_id=${me.id}&status=published`),
      api.get('/quiz-attempts'),
    ]).then(([mine, attempts]) => {
      if (!alive) return;
      setQuizzes(mine || []);
      const byQuiz = {};
      (attempts || []).forEach(a => {
        const prev = byQuiz[a.quiz_id];
        if (!prev || (a.submitted_at || '') > (prev.submitted_at || '')) byQuiz[a.quiz_id] = a;
      });
      setAttemptsByQuiz(byQuiz);
    }).catch(e => alive && setError(friendlyError(e, 'Could not load quizzes.')));
    return () => { alive = false; };
  }, [me?.id]);

  const sorted = useMemo(() => {
    if (!quizzes) return [];
    return [...quizzes].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [quizzes]);

  return (
    <div className="space-y-5 max-w-3xl" data-testid="my-quizzes-page">
      <button onClick={() => navigate('/student/exam-prep')} data-testid="my-quizzes-back-to-exam-prep" className="text-xs font-semibold inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft className="w-4 h-4" /> Back to Exam Prep
      </button>
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold opacity-60">Exam Prep</div>
          <h1 className="text-3xl font-semibold leading-tight" style={{ color: 'var(--st-ink)' }}>
            Practice
          </h1>
<AccentRule className="mt-2" />
        </div>
        <button
          onClick={() => navigate('/student/quizzes/create')}
          data-testid="my-quizzes-create-button"
          className="rounded-xl px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5 shrink-0"
          style={{ background: colors.gold, color: '#101826' }}>
          <Plus className="w-4 h-4" /> Create Test
        </button>
      </header>

      {error && (
        <div className="rounded-2xl border p-4 text-sm flex items-center gap-2" style={{ borderColor: '#FCA5A5', color: '#B91C1C', background: '#FEF2F2' }}>
          <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {quizzes === null && !error && (
        <Loader label="Loading your tests…" />
      )}

      {quizzes !== null && sorted.length === 0 && !error && (
        <div className="rounded-2xl border p-8 text-center" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="my-quizzes-empty">
          <ClipboardList className="w-8 h-8 mx-auto opacity-30 mb-2" />
          <p className="text-sm mb-3" style={{ color: 'var(--st-mute)' }}>No tests yet — create one to start practicing.</p>
          <button onClick={() => navigate('/student/quizzes/create')} data-testid="my-quizzes-empty-create-button"
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold" style={{ background: colors.gold, color: '#101826' }}>
            <Plus className="w-4 h-4" /> Create Test
          </button>
        </div>
      )}

      <ul className="space-y-2">
        {sorted.map(q => {
          const attempt = attemptsByQuiz[q.id];
          return (
            <li key={q.id}>
              <button
                onClick={() => navigate(`/student/quizzes/${q.id}`)}
                data-testid={`my-quiz-item-${q.id}`}
                className="w-full text-left rounded-2xl p-4 border flex items-center gap-3 transition hover:shadow-md"
                style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: colors.goldTint, color: colors.gold }}>
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ fontFamily: 'var(--st-font-heading)' }}>{q.title}</div>
                  <div className="text-xs opacity-60 mt-0.5 flex items-center gap-2 flex-wrap">
                    {q.exam_mode && (
                      <span className="font-semibold" style={{ color: colors.gold }}>{q.exam_mode.replace('_', ' ')}</span>
                    )}
                    {q.time_limit && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {q.time_limit} min</span>}
                    <span>{(q.questions || []).length} question{(q.questions || []).length === 1 ? '' : 's'}</span>
                  </div>
                </div>
                {attempt ? (
                  <span className="text-xs font-semibold rounded-full px-2.5 py-1 inline-flex items-center gap-1 shrink-0" style={{ background: '#ECFDF5', color: '#059669' }}>
                    <Award className="w-3 h-3" /> {Math.round(attempt.percentage || 0)}%
                  </span>
                ) : (
                  <span className="text-xs font-semibold rounded-full px-2.5 py-1 shrink-0" style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)', color: 'var(--st-mute)' }}>
                    New
                  </span>
                )}
                <ChevronRight className="w-4 h-4 opacity-40 shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
